/**
 * The authored-token intake — how every word a vocabulary declares is read
 * before it is judged. Split out of `compile-lexicon.ts` at the 500-line cap
 * (D227), so the bare-direction check reads a verb's declared direction the
 * way the compiler reads every other authored token.
 *
 * @packageDocumentation
 */

import { foldDiacritics } from '../normalize/fold-characters.js';

/**
 * Trim + lowercase one authored token (generous intake; collisions that emerge
 * from folding are reported like any other), its diacritics folded as the
 * player's input is (D226, E13) — a vocabulary spelling `café` meets a typed
 * `cafe`.
 *
 * @param rawToken - The authored token text
 * @returns The normalized token
 */
export const normalizeAuthoredToken = (rawToken: string): string => foldDiacritics(rawToken.trim().toLowerCase());

/**
 * Is this a usable single-word token?
 *
 * @param tokenText - The normalized candidate
 * @returns Whether it is non-blank and space-free
 */
export const isValidSingleWord = (tokenText: string): boolean => tokenText !== '' && !tokenText.includes(' ');

/** The most words one preposition binding may span (D242: `up to`, read as one word in its verb's clause). */
export const PREPOSITION_WORD_LIMIT = 2;

/** Shared issue text for authored tokens containing `:` (reserved for parser builtins; folding strips it from input). */
export const COLON_TOKEN_ISSUE =
	'contains ":", which players can never type (character folding strips it); ":" is reserved for parser builtin tokens';
